
/* global debounce */

if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.fabianHome = this.dataset.fabianHomeHero === 'true';
      if (this.fabianHome && (this.homeInitialized || !this.isConnected)) return;
      if (this.fabianHome) {
        this.homeInitialized = true;
        this.homeListeners = [];
        this.configuredAutoplay ??= this.dataset.autoplay === 'true';
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reducedMotion = this.motionQuery.matches;
        this.motionHandler = (event) => {
          this.reducedMotion = event.matches;
          this.reducedMotion ? this.stopAutoplay() : this.resumeAutoplay();
          this.updateHomePlayButton();
        };
        this.motionQuery.addEventListener('change', this.motionHandler);
      }
      this.slideshow = this.querySelector('.slideshow');
      this.slides = this.querySelectorAll('.slideshow__slide');
      this.nav = this.querySelector('.slideshow-nav');
      this.pagination = this.querySelector('.slideshow-pagination');
      this.rtl = document.dir === 'rtl';
      this.currentIndex = 0;
      this.swipeThreshold = 80;
      this.wheelSensitivity = 15;

      if (this.nav) {
        this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
        this.listen(this.nav, 'click', this.handleNavClick.bind(this));
      }

      if (this.pagination) {
        this.pageBtns = this.querySelectorAll('.page-btn');
        this.listen(this.pagination, 'click', this.handlePaginationClick.bind(this));
      }

      if (theme.device.hasTouch) {
        this.listen(this.slideshow, 'touchstart', this.handleTouchStart.bind(this), { passive: true });
        if (this.fabianHome) {
          this.listen(this.slideshow, 'touchend', this.handleHomeTouchEnd.bind(this), { passive: true });
          this.listen(this.slideshow, 'touchcancel', () => { this.homeTouch = null; }, { passive: true });
          this.listen(this.slideshow, 'click', (event) => {
            if (Date.now() < (this.suppressClickUntil || 0) && event.target.closest('a')) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          }, true);
        } else {
          this.listen(this.slideshow, 'touchmove', debounce(this.handleTouchMove.bind(this), 200), { passive: true });
        }
      }

      this.listen(this.slideshow, 'wheel', this.handleWheel.bind(this), { passive: false });

      this.listen(this.slideshow, 'keydown', this.handleKeyDown.bind(this));

      this.listen(this.slideshow, 'transitionend', SlideShow.handleTransitionend);

      this.setSlideVisibility();
      if (this.fabianHome) {
        this.listen(window, 'on:debounced-resize', this.alignHomeSlide.bind(this));
        this.alignHomeSlide();
      }
      if (this.fabianHome ? this.configuredAutoplay : this.dataset.autoplay === 'true') this.initAutoPlay();
    }

    listen(target, name, handler, options) {
      target.addEventListener(name, handler, options);
      if (this.fabianHome) this.homeListeners.push([target, name, handler, options]);
    }

    connectedCallback() {
      if (this.homeDisconnected) { this.homeDisconnected = false; this.init(); }
    }

    disconnectedCallback() {
      if (!this.fabianHome) return;
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.setActiveSlideTimeoutId);
      this.homeListeners.forEach(([target, name, handler, options]) => target.removeEventListener(name, handler, options));
      this.motionQuery.removeEventListener('change', this.motionHandler);
      if (this.autoplayObserver) this.autoplayObserver.disconnect();
      this.homeInitialized = false;
      this.homeDisconnected = true;
    }

    alignHomeSlide() {
      const controls = this.querySelector('.slideshow-ctrl');
      if (controls) this.style.setProperty('--fabian-controls-height', `${controls.offsetHeight}px`);
      this.slideshow.scrollTo({ left: this.slides[this.currentIndex].offsetLeft - this.slides[0].offsetLeft, behavior: 'auto' });
    }

    updateHomePlayButton() {
      const button = this.querySelector('.autoplay-btn');
      if (!button) return;
      button.classList.toggle('is-paused', !this.autoplayEnabled || this.autoplayPaused);
      button.disabled = this.reducedMotion;
    }

    handleHomeTouchEnd(event) {
      if (!this.homeTouch || event.changedTouches[0].identifier !== this.homeTouch.id) return;
      const touch = this.homeTouch;
      this.homeTouch = null;
      const dx = event.changedTouches[0].screenX - touch.x;
      const dy = event.changedTouches[0].screenY - touch.y;
      if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy)) return;
      this.suppressClickUntil = Date.now() + 500;
      (dx > 0) === this.rtl ? this.showNextSlide() : this.showPrevSlide();
    }

    initAutoPlay() {
      this.autoplayEnabled = this.fabianHome ? !this.userPaused : true;
      this.autoplaySpeed = this.dataset.speed * 1000;

      if (this.fabianHome) {
        this.listen(this, 'mouseenter', () => { this.homeHovered = true; this.pauseAutoplay(); });
        this.listen(this, 'mouseleave', () => { this.homeHovered = false; this.resumeAutoplay(); });
        this.listen(this, 'focusin', () => { this.focusPaused = true; this.pauseAutoplay(); });
      } else {
        this.listen(this, 'mouseover', this.pauseAutoplay.bind(this));
        this.listen(this, 'mouseleave', this.resumeAutoplay.bind(this));
        this.listen(this.slideshow, 'focusin', this.pauseAutoplay.bind(this));
        this.listen(this.slideshow, 'focusout', this.resumeAutoplay.bind(this));
      }

      this.addObserver();
      this.startAutoplay();
    }

    /**
     * Handles 'click' events on the nav buttons container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (this.fabianHome) {
        const button = evt.target.closest('button');
        if (!button || !this.nav.contains(button)) return;
        if (button.matches('.autoplay-btn')) { this.togglePlayState({ target: button }); return; }
        if (button.name === 'next') this.showNextSlide();
        if (button.name === 'prev') this.showPrevSlide();
        return;
      }
      if (evt.target.matches('.autoplay-btn')) {
        this.togglePlayState(evt);
        return;
      }

      if (evt.target.name === 'next') {
        this.showNextSlide();
      } else if (evt.target.name === 'prev') {
        this.showPrevSlide();
      }
    }

    /**
     * Handles 'click' events on the pagination.
     * @param {object} evt - Event object.
     */
    handlePaginationClick(evt) {
      if (!evt.target.matches('.page-btn')) return;
      this.autoplayTimeLeft = this.autoplaySpeed;
      this.setActiveSlide(Number(evt.target.dataset.index));
    }

    /**
     * Handles 'wheel' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleWheel(evt) {
      if (this.fabianHome) {
        if (Math.abs(evt.deltaX) <= Math.abs(evt.deltaY) || Math.abs(evt.deltaX) <= this.wheelSensitivity) return;
        evt.preventDefault();
        (evt.deltaX > 0) === this.rtl ? this.showPrevSlide() : this.showNextSlide();
        return;
      }
      /*
        In theory, preventDefault on wheel events should stop scroll events entirely.
        This is the case, however a fast scroll will still register a deltaX value.
        This is useful as it allows us to prevent the scroll but still trigger the
        slide change on a prominent scroll. It shouldn't work - but it does?
      */
      if (evt.deltaX !== 0) evt.preventDefault();

      /*
        Consider throttling? timeout already applied to slide changes...
      */
      const isHorizontal = Math.abs(evt.deltaX) > Math.abs(evt.deltaY);
      const isHorizontalSignificant = Math.abs(evt.deltaX) > this.wheelSensitivity;

      if (isHorizontal && isHorizontalSignificant) {
        evt.deltaX > 0 ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    /**
     * Handles 'keyDown' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleKeyDown(evt) {
      if (this.fabianHome) {
        if (evt.altKey || evt.ctrlKey || evt.metaKey || evt.target.closest('input, textarea, select, [contenteditable="true"]')) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(evt.key)) return;
        evt.preventDefault();
        if (evt.key === 'Home') this.setActiveSlide(0);
        else if (evt.key === 'End') this.setActiveSlide(this.slides.length - 1);
        else if ((evt.key === 'ArrowLeft') === this.rtl) this.showNextSlide();
        else this.showPrevSlide();
        return;
      }
      if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') {
        evt.preventDefault();
        if (evt.key === 'ArrowRight') {
          this.showNextSlide();
        }
        if (evt.key === 'ArrowLeft') {
          this.showPrevSlide();
        }
      }
    }

    /**
     * Handles 'touchstart' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchStart(evt) {
      if (this.fabianHome) {
        this.homeTouch = null;
        this.suppressClickUntil = 0;
        if (evt.touches.length !== 1 || evt.target.closest('button, input, textarea, select')) return;
        const touch = evt.changedTouches[0];
        this.homeTouch = { x: touch.screenX, y: touch.screenY, id: touch.identifier };
        return;
      }
      this.touchStart = evt.changedTouches[0].screenX;
    }

    /**
     * Handles 'touchmove' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchMove(evt) {
      const touchEnd = evt.changedTouches[0].screenX;

      if (touchEnd < this.touchStart - this.swipeThreshold) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      }

      if (touchEnd > this.touchStart + this.swipeThreshold) {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    /**
     * Handles 'transitionend' events on the slideshow.
     * @param {object} evt - Event object.
     */
    static handleTransitionend(evt) {
      if (!evt.target.matches('.has-motion')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      let index = this.currentIndex + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      let index = this.currentIndex - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    /**
     * Sets the active slide.
     * @param {number} slideIndex - Index of the slide to show.
     * @param {string} slideDirection - Direction for slide transitions.
     */
    setActiveSlide(slideIndex, slideDirection) {
      if (this.fabianHome && (!Number.isInteger(slideIndex) || slideIndex < 0 || slideIndex >= this.slides.length)) return;
      if (slideIndex === this.currentIndex) {
        if (this.fabianHome) {
          clearTimeout(this.setActiveSlideTimeoutId);
          this.slides[this.currentIndex].classList.remove('transition-out');
          if (this.autoplayEnabled && !this.autoplayPaused) {
            clearTimeout(this.autoplayTimer);
            this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
          }
        }
        return;
      }

      // Cancel any prior calls to this that have not completed
      clearTimeout(this.setActiveSlideTimeoutId);

      // Clear autoplay timer
      clearTimeout(this.autoplayTimer);

      // Set data attribute for direction of slide transitions.
      let dir = slideDirection || (slideIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) dir = dir === 'next' ? 'prev' : 'next';
      this.dataset.direction = dir;

      // Trigger text overlay transition out.
      this.slides[this.currentIndex].classList.add('transition-out');

      this.setActiveSlideTimeoutId = setTimeout(() => {
        // Move new slide into the viewport.
        this.slideshow.scrollTo({ left: this.fabianHome ? this.slides[slideIndex].offsetLeft - this.slides[0].offsetLeft : this.slides[slideIndex].offsetLeft, behavior: 'auto' });

        // Update current slide and trigger transitions.
        this.slides[slideIndex].classList.add('is-active');
        this.slides[this.currentIndex].classList.remove('is-active', 'transition-out');

        // Add class to transitioned content element (if it exists).
        const transitionedEl = this.slides[this.currentIndex].querySelector('.has-motion');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        // Reset autoplay slide timer.
        if (this.autoplayEnabled) this.autoplayStartTime = Date.now();

        // Update slide counter.
        if (this.counterCurrent) this.counterCurrent.textContent = slideIndex + 1;

        this.currentIndex = slideIndex;
        this.setSlideVisibility();
        this.updatePagination();

        // Autoplay
        if (this.autoplayEnabled && !this.autoplayPaused) {
          this.autoplayTimeLeft = this.autoplaySpeed;
          this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
        }
      }, this.fabianHome && this.reducedMotion ? 0 : 200);
    }

    /**
     * Updates the 'aria-current' attribute of the pagination buttons.
     */
    updatePagination() {
      if (!this.pagination) return;

      this.pageBtns.forEach((el, index) => {
        if (index === this.currentIndex) {
          el.setAttribute('aria-current', 'true');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    stopAutoplay() {
      this.setSlideshowState('paused');
      clearTimeout(this.autoplayTimer);
    }

    startAutoplay() {
      if (this.fabianHome && (this.reducedMotion || !this.autoplayEnabled)) { this.stopAutoplay(); return; }
      this.autoplayTimeLeft = null;
      this.autoplayStartTime = Date.now();
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
      this.setSlideshowState('running');
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.stopAutoplay();

      if (this.autoplayTimeLeft) {
        this.autoplayTimeLeft -= (Date.now() - this.resumedTime);
      } else {
        this.autoplayTimeLeft = this.autoplaySpeed - (Date.now() - this.autoplayStartTime);
      }
    }

    resumeAutoplay() {
      if (this.fabianHome && (this.reducedMotion || this.focusPaused || this.homeHovered || this.homeVisible === false)) return;
      if (!this.autoplayEnabled || !this.autoplayPaused) return;

      this.resumedTime = Date.now();
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplayTimeLeft);

      this.setSlideshowState('running');
    }

    /**
     * Adds an observer to pause autoplay when the slideshow is not in the viewport.
     */
    addObserver() {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (this.fabianHome) this.homeVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.resumeAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      });

      observer.observe(this);
      if (this.fabianHome) this.autoplayObserver = observer;
    }

    /**
     * Sets 'aria-hidden' and 'tabindex' of the slides and text overlay buttons.
     */
    setSlideVisibility() {
      this.slides.forEach((el, index) => {
        const btns = el.querySelectorAll('a');

        if (index === this.currentIndex) {
          el.setAttribute('aria-hidden', 'false');
          el.removeAttribute('tabindex');

          btns.forEach((btn) => {
            btn.removeAttribute('tabindex');
          });
        } else {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');

          btns.forEach((btn) => {
            btn.setAttribute('tabindex', '-1');
          });
        }
      });
    }

    /**
     * Sets 'aria-live' state of the slideshow and 'play-state' of the pagination animation.
     * @param {string} state - State to set ('running' or 'paused').
     */
    setSlideshowState(state) {
      this.style.setProperty('--play-state', state);
      this.slideshow.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
      this.autoplayPaused = state === 'paused';
      if (this.fabianHome) {
        this.dataset.autoplay = state === 'running' ? 'true' : 'false';
        this.updateHomePlayButton();
      }
    }

    /**
     * Toggles the autoplay state.
     * @param {object} evt - Event object.
     */
    togglePlayState(evt) {
      if (this.fabianHome) {
        if (this.reducedMotion) return;
        this.userPaused = this.autoplayEnabled && !this.autoplayPaused;
        this.autoplayEnabled = !this.userPaused;
        this.focusPaused = false;
        this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
        return;
      }
      evt.target.classList.toggle('is-paused');
      this.autoplayEnabled = !this.autoplayEnabled;
      this.dataset.autoplay = this.autoplayEnabled ? 'true' : 'false';
      this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
    }
  }

  customElements.define('slide-show', SlideShow);
}